import React from "react";
import { Star, Quote, ExternalLink, Award } from "lucide-react";
import { useLang } from "@/lib/i18n";

/**
 * GoogleReviews — Sociale bewijssectie met geverifieerde Google reviews.
 *
 * Alle reviews hieronder zijn handmatig overgenomen uit het publieke
 * Google Business profiel van Wasz.nl (5,0 ★ · 14 reviews totaal).
 * Geen API, geen fictieve quotes — 100% authentiek.
 *
 * Google reviews-link (alle 14 live):
 *   https://www.google.com/search?q=wasz.nl#lrd=0x47c6170dd3027b7b:0x62a6ae4c17f0610b,1
 */

const GOOGLE_REVIEWS_URL =
  "https://www.google.com/search?q=wasz.nl#lrd=0x47c6170dd3027b7b:0x62a6ae4c17f0610b,1";

const TOTAL_GOOGLE_REVIEWS = 14;
const AVG_RATING = "5,0";

const REVIEWS = [
  {
    id: "hans-de-jonge",
    name: "Hans de Jonge",
    badge: { nl: "Local Guide · 198 reviews", en: "Local Guide · 198 reviews" },
    date: { nl: "Recent", en: "Recent" },
    rating: 5,
    initials: "HJ",
    accent: "#F97316",
    text: {
      nl: "Wij doen al een aantal jaren zaken met Wasz.nl en zijn uitermate tevreden met deze firma. Komen altijd hun afspraken na.",
      en: "We have been doing business with Wasz.nl for several years now and are extremely satisfied with this company. They always keep their appointments.",
    },
    context: { nl: "Vaste zakelijke klant", en: "Long-standing business client" },
  },
  {
    id: "remy-aust",
    name: "Rémy Aust",
    badge: { nl: "Local Guide · 26 reviews", en: "Local Guide · 26 reviews" },
    date: { nl: "29 april 2025", en: "29 April 2025" },
    rating: 5,
    initials: "RA",
    accent: "#EF4444",
    text: {
      nl: "Gisteren m'n bus laten wassen op locatie, helemaal tevreden!",
      en: "Had my van washed on-site yesterday, completely satisfied!",
    },
    context: { nl: "Mobiele wasbeurt op locatie", en: "Mobile wash on-site" },
  },
  {
    id: "lillo-marchese",
    name: "Lillo Marchese",
    badge: { nl: "Google review", en: "Google review" },
    date: { nl: "Recent", en: "Recent" },
    rating: 5,
    initials: "LM",
    accent: "#94A3B8",
    text: {
      nl: "Goede service en schoon en strak afgeleverde vrachtwagens! Nauwkeurig en vriendelijk.",
      en: "Good service, trucks delivered clean and crisp. Accurate and friendly.",
    },
    context: { nl: "Vrachtwagenreiniging", en: "Truck cleaning" },
  },
  {
    id: "joshua-ram",
    name: "Joshua Ram",
    badge: { nl: "Local Guide · 28 reviews", en: "Local Guide · 28 reviews" },
    date: { nl: "Recent", en: "Recent" },
    rating: 5,
    initials: "JR",
    accent: "#DC2626",
    text: {
      nl: "Goeie gasten. Werken snel en efficiënt! Echt een aanrader!",
      en: "Great guys. Fast and efficient! Truly recommended!",
    },
    context: { nl: "Snelle service", en: "Fast service" },
  },
  {
    id: "jeremy-nl",
    name: "Jeremy_nl",
    badge: { nl: "Google review", en: "Google review" },
    date: { nl: "Recent", en: "Recent" },
    rating: 5,
    initials: "JN",
    accent: "#7F1D1D",
    text: {
      nl: "Zeer klantvriendelijk en betrouwbaar. Wassen nauwkeurig en goed. Ben zeer tevreden.",
      en: "Very customer-friendly and reliable. They wash accurately and well. I'm very satisfied.",
    },
    context: { nl: "Klantvriendelijkheid", en: "Customer friendliness" },
  },
];

const StarRow = ({ rating = 5, size = 14 }) => (
  <div className="flex items-center gap-0.5" aria-label={`${rating} van 5 sterren`}>
    {Array.from({ length: 5 }).map((_, i) => (
      <Star
        key={i}
        style={{ width: size, height: size }}
        className={i < rating ? "fill-[#FFC83D] text-[#FFC83D]" : "text-white/15"}
        strokeWidth={1.5}
      />
    ))}
  </div>
);

const Avatar = ({ initials, accent }) => (
  <div
    className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0"
    style={{ backgroundColor: accent }}
    aria-hidden="true"
  >
    {initials}
  </div>
);

const ReviewCard = ({ r, lang }) => {
  const text = r.text[lang] || r.text.nl;
  const date = r.date[lang] || r.date.nl;
  const badge = r.badge[lang] || r.badge.nl;
  const context = r.context[lang] || r.context.nl;
  const isLocalGuide = badge.toLowerCase().includes("local guide");

  return (
    <article
      data-testid={`review-card-${r.id}`}
      className="group relative rounded-2xl border border-white/10 bg-[#0F0F23] p-6 sm:p-7 flex flex-col h-full hover:border-[#00D4FF]/30 transition-colors duration-300"
    >
      <Quote
        className="absolute top-5 right-5 w-7 h-7 text-[#00D4FF]/12 rotate-180"
        strokeWidth={1.5}
        aria-hidden="true"
      />

      <div className="flex items-center gap-3 mb-4">
        <Avatar initials={r.initials} accent={r.accent} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="font-semibold text-white text-[15px] truncate">{r.name}</span>
            {isLocalGuide && (
              <Award className="w-3.5 h-3.5 text-[#00D4FF] shrink-0" strokeWidth={2} aria-label="Local Guide" />
            )}
          </div>
          <div className="text-[11px] text-white/45 mt-0.5 truncate">{badge}</div>
        </div>
      </div>

      <div className="flex items-center gap-2 mb-4">
        <StarRow rating={r.rating} />
        <span className="text-[11px] text-white/40">· {date}</span>
      </div>

      <p className="text-white/85 leading-relaxed text-[15px] flex-1">“{text}”</p>

      <div className="mt-5 pt-4 border-t border-white/5 text-[11px] uppercase tracking-[0.14em] text-white/35 font-medium">
        {context}
      </div>
    </article>
  );
};

const ProfileCTA = ({ lang }) => (
  <a
    href={GOOGLE_REVIEWS_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid="reviews-google-cta"
    className="group relative rounded-2xl border border-dashed border-[#00D4FF]/30 bg-gradient-to-br from-[#0F0F23] to-[#0a0a18] p-6 sm:p-7 flex flex-col h-full hover:border-[#00D4FF]/60 transition-all duration-300"
  >
    <div className="flex items-center gap-2 mb-4">
      <div className="w-10 h-10 rounded-full bg-white flex items-center justify-center shrink-0">
        {/* Google "G" mark */}
        <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.56c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.56-2.76c-.99.66-2.25 1.06-3.72 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A10.99 10.99 0 0 0 12 23z" fill="#34A853"/>
          <path d="M5.84 14.11A6.6 6.6 0 0 1 5.48 12c0-.73.13-1.44.36-2.11V7.05H2.18A10.99 10.99 0 0 0 1 12c0 1.77.43 3.45 1.18 4.95l3.66-2.84z" fill="#FBBC05"/>
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.05l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" fill="#EA4335"/>
        </svg>
      </div>
      <span className="text-xs uppercase tracking-[0.16em] text-white/55 font-semibold">
        Google Reviews
      </span>
    </div>

    <div className="flex-1 flex flex-col justify-center">
      <div className="flex items-baseline gap-2">
        <div className="text-5xl font-black text-white tracking-tight leading-none">
          {AVG_RATING}
        </div>
        <StarRow rating={5} size={16} />
      </div>
      <div className="mt-3 text-sm text-white/65">
        {lang === "nl"
          ? `Gebaseerd op ${TOTAL_GOOGLE_REVIEWS} reviews`
          : `Based on ${TOTAL_GOOGLE_REVIEWS} reviews`}
      </div>
      <div className="mt-2 text-xs text-white/40">
        {lang === "nl"
          ? "100% van de klanten gaf 5 sterren"
          : "100% of customers gave 5 stars"}
      </div>
    </div>

    <div className="mt-5 pt-4 border-t border-white/5 flex items-center justify-between">
      <span className="text-sm font-semibold text-[#00D4FF]">
        {lang === "nl" ? "Bekijk alle reviews" : "View all reviews"}
      </span>
      <ExternalLink className="w-4 h-4 text-[#00D4FF] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </div>
  </a>
);

const GoogleReviews = () => {
  const { lang } = useLang();

  return (
    <section
      data-testid="home-reviews-section"
      id="reviews"
      className="py-24 sm:py-32 relative overflow-hidden"
    >
      <div className="absolute inset-0 cyber-grid opacity-10 pointer-events-none" />
      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6 mb-14">
          <div className="max-w-2xl">
            <p className="section-overline mb-3" data-testid="reviews-overline">
              {lang === "nl" ? "Klantbeoordelingen" : "Customer reviews"}
            </p>
            <h2 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white leading-tight">
              {lang === "nl" ? (
                <>
                  <span className="text-[#00D4FF]">5,0 sterren</span> op Google — en dat voelen onze klanten.
                </>
              ) : (
                <>
                  <span className="text-[#00D4FF]">5.0 stars</span> on Google — and our clients feel it.
                </>
              )}
            </h2>
            <p className="mt-5 text-white/55 max-w-xl leading-relaxed">
              {lang === "nl"
                ? `Een selectie van de ${TOTAL_GOOGLE_REVIEWS} publieke beoordelingen op ons Google Business profiel. Allemaal letterlijk overgenomen — geen herschrijving, geen filtering.`
                : `A selection of the ${TOTAL_GOOGLE_REVIEWS} public reviews on our Google Business profile. All quoted verbatim — no rewriting, no filtering.`}
            </p>
          </div>

          <div className="flex items-center gap-3 text-white/65 text-sm shrink-0">
            <StarRow rating={5} size={18} />
            <div className="flex flex-col">
              <span>
                <span className="font-bold text-white text-base">{AVG_RATING}</span>
                <span className="text-white/45"> / 5,0</span>
              </span>
              <span className="text-[11px] text-white/40">
                {TOTAL_GOOGLE_REVIEWS} {lang === "nl" ? "Google reviews" : "Google reviews"}
              </span>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {REVIEWS.map((r) => (
            <ReviewCard key={r.id} r={r} lang={lang} />
          ))}
          <ProfileCTA lang={lang} />
        </div>

        <p
          data-testid="reviews-disclaimer"
          className="mt-8 text-xs text-white/35 max-w-2xl"
        >
          {lang === "nl"
            ? "Reviews zijn letterlijk overgenomen van ons publieke Google Business profiel. Klik op de Google-tegel voor het volledige overzicht en de meest recente beoordelingen."
            : "Reviews are quoted verbatim from our public Google Business profile. Click the Google tile for the full overview and most recent feedback."}
        </p>
      </div>
    </section>
  );
};

// JSON-LD AggregateRating — geëxporteerd voor SEO injectie via useSEO.
// reviewCount = totaal publiek aantal reviews; embedded reviews = 5 met body.
export const reviewsAggregateJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "Wasz.nl",
  url: "https://wasz.nl/",
  aggregateRating: {
    "@type": "AggregateRating",
    ratingValue: "5.0",
    bestRating: "5",
    worstRating: "1",
    ratingCount: String(TOTAL_GOOGLE_REVIEWS),
    reviewCount: String(TOTAL_GOOGLE_REVIEWS),
  },
  review: [
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Hans de Jonge" },
      reviewBody:
        "Wij doen al een aantal jaren zaken met Wasz.nl en zijn uitermate tevreden met deze firma. Komen altijd hun afspraken na.",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Rémy Aust" },
      datePublished: "2025-04-29",
      reviewBody: "Gisteren m'n bus laten wassen op locatie, helemaal tevreden!",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Lillo Marchese" },
      reviewBody:
        "Goede service en schoon en strak afgeleverde vrachtwagens! Nauwkeurig en vriendelijk.",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Joshua Ram" },
      reviewBody: "Goeie gasten. Werken snel en efficiënt! Echt een aanrader!",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
    },
    {
      "@type": "Review",
      author: { "@type": "Person", name: "Jeremy_nl" },
      reviewBody:
        "Zeer klantvriendelijk en betrouwbaar. Wassen nauwkeurig en goed. Ben zeer tevreden.",
      reviewRating: { "@type": "Rating", ratingValue: "5", bestRating: "5", worstRating: "1" },
    },
  ],
};

export default GoogleReviews;
